import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AppointmentUiComponent } from '../appointment-ui/appointment-ui.component';
import { commonImports } from '../shared/table-interface';
import { MatDialog } from '@angular/material/dialog';
import { AddMedicationComponent } from '../medication2/add-medication/add-medication.component';
import { ScrolldDirective } from '../scrolld.directive';
import { fieldType } from '../shared/dynamic-forms.interface2';
import { Bundle, Medication } from 'fhir/r4';
import { HttpClient } from '@angular/common/http';
import { TabledOptionComponent } from '../tabled-option/tabled-option.component';
import { DummyMedicationRequestDetailsComponent } from '../dummy-medication-request-details/dummy-medication-request-details.component';
import { DetailzViewzComponent, DetailActionButton } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { CardzDetailsCheckComponent } from '../cardz-details-check/cardz-details-check.component';
import { PharmacyDashboardService } from './pharmacy-dashboard.service';
import { map, shareReplay } from 'rxjs';
import { Router } from '@angular/router';
import { UtilityService } from '../shared/utility.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { UpcomingActivitiesComponent } from '../unified-activities/upcoming-activities.component';

@Component({
  selector: 'app-pharmacy-dashboard',
  standalone: true,
  host: {
    class: 'pharmacy-dashboard',
  },
  imports: [
    AppointmentUiComponent,
    CardzDetailsCheckComponent,
    ...commonImports,
    ScrolldDirective,
    TabledOptionComponent,UpcomingActivitiesComponent,
    EmptyStateComponent
  ],
  templateUrl: './pharmacy-dashboard.component.html',
  styleUrls: ['../admin-dashboard/admin-dashboard.component.scss', './pharmacy-dashboard.component.scss']
})
export class PharmacyDashboardComponent implements OnInit {

  private dialog = inject(MatDialog);
  private pharmacyService = inject(PharmacyDashboardService);
  private router = inject(Router);
  private cd = inject(ChangeDetectorRef);
  private utilityService = inject(UtilityService);

  metrics$ = this.pharmacyService.getMetrics().pipe(shareReplay(1));

  newPrescriptions$ = this.metrics$.pipe(map(m => m.newPrescriptions));
  pendingDispenses$ = this.metrics$.pipe(map(m => m.pendingDispenses));
  lowStockItems$ = this.metrics$.pipe(map(m => m.lowStockItems));
  expiredItems$ = this.metrics$.pipe(map(m => m.expiredItems));
  totalItems$ = this.metrics$.pipe(map(m => m.totalItems));

  expiredMedications$ = this.pharmacyService.getExpiredMedications().pipe(
    map(b => this.transformMedicationForTable(b)),
    shareReplay(1)
  );

  lowStockMedications$ = this.pharmacyService.getLowStockMedications().pipe(
    map(b => this.transformMedicationForTable(b)),
    shareReplay(1)
  );

  private transformMedicationForTable(bundle: Bundle<Medication>): Bundle<any> {
    if (!bundle?.entry) return bundle;
    const transformedEntries = bundle.entry.map(e => {
      const med = e.resource as Medication;
      const stock = this.utilityService.getMedicationTotalRemaining(med);
      const expiry = med.batch?.expirationDate ? new Date(med.batch.expirationDate).toLocaleDateString() : 'N/A';
      const inStock = stock ? `${stock.value} ${stock.unit || ''}` : 'N/A';

      return {
        ...e,
        resource: {
          ...med,
          name: med.code?.text || med.id,
          expiry,
          inStock
        }
      };
    });
    return { ...bundle, entry: transformedEntries };
  }

  expiredTableConfig = {
    columnMetaData: new Map([
      ['name', { dataType: "IndividualField", columnName: "Medication Name" }],
      ['status', { dataType: "SingleCodeField", displayStyle: "chips", columnName: "Status" }],
      ['expiry', { dataType: "IndividualField", columnName: "Expiry Date" }],
    ]) as Map<string, { dataType?: fieldType; displayStyle?: any; columnName?: string }>,
    columns: ['name', 'expiry', 'status'],
  };

  lowStockTableConfig = {
    columnMetaData: new Map([
      ['name', { dataType: "IndividualField", columnName: "Medication Name" }],
      ['status', { dataType: "SingleCodeField", displayStyle: "chips", columnName: "Status" }],
      ['inStock', { dataType: "IndividualField", columnName: "In Stock" }],
    ]) as Map<string, { dataType?: fieldType; displayStyle?: any; columnName?: string }>,
    columns: ['name', 'inStock', 'status'],
  };

  addMedicine() {
    this.dialog.open(AddMedicationComponent, {
      maxWidth: '650px',
      maxHeight: '93vh',
    });
  }

  navigateToMedicineRequests() {
    this.router.navigate(['/app/medicine-requests']);
  }

  navigateToMedicineStock() {
    this.router.navigate(['/app/medicine-stock']);
  }

  navigateToMedicationDispense() {
    this.router.navigate(['/app/medication-dispense']);
  }

  lowStockLoaded = false;

  ngOnInit() {
    // Logic moved to observables
  }

  processSelectedRow(ev: any) {
    const row = ev?.resource ?? ev;
    const med = row.resource ?? row;

    // Prefer exact id match when available, otherwise use code (coding code or text)
    const queryParams: any = {};
    if (med?.id) {
      queryParams['_id'] = med.id;
    } else if (med?.code?.coding?.[0]?.code) {
      queryParams['code'] = med.code.coding[0].code;
    } else if (med?.code?.text) {
      queryParams['code'] = med.code.text;
    }

    // Navigate to the medication list (medicine stock) and let that component pick up query params
    this.router.navigate(['/app/medicine-stock'], { queryParams });
  }

  processLoaded() {
    setTimeout(() => {
      this.lowStockLoaded = true;
      this.cd.detectChanges();
    }, 100);
  }
}
